<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  seasons: {
    type: Array,
    required: true
  },
  selectedSeason: {
    type: Array,
    default: () => []
  },
  selectedLeagues: {
    type: Array,
    default: () => []
  },
  availableLeagues: {
    type: Array,
    default: () => []
  },
  selectedAdditionalFilter: {
    type: String,
    default: 'allGames'
  }
})

const emit = defineEmits(['update:selectedSeason', 'update:selectedLeagues', 'update:additionalFilter'])

// Search functionality
const leagueSearch = ref('')

// Section expand/collapse state
const isAdditionalFiltersExpanded = ref(true)
const isSeasonsExpanded = ref(true)
const isLeaguesExpanded = ref(true)

const safeSelectedLeagues = computed(() => Array.isArray(props.selectedLeagues) ? props.selectedLeagues : [])
const safeSelectedSeason = computed(() => Array.isArray(props.selectedSeason) ? props.selectedSeason : [])

// Filtered leagues based on search
const filteredLeagues = computed(() => {
  if (!leagueSearch.value.trim()) {
    return props.availableLeagues
  }
  
  const searchTerm = leagueSearch.value.toLowerCase().trim()
  return props.availableLeagues.filter(league => 
    league.name.toLowerCase().includes(searchTerm) ||
    league.id.toLowerCase().includes(searchTerm)
  )
})

// Debug log to see what's being passed
console.log('=== PlayerDetailsSidebar received ===')
console.log('selectedSeason:', props.selectedSeason)
console.log('selectedLeagues:', props.selectedLeagues)
console.log('selectedLeagues type:', typeof props.selectedLeagues)
console.log('selectedLeagues length:', props.selectedLeagues?.length)
console.log('safeSelectedSeason:', safeSelectedSeason.value)
console.log('safeSelectedLeagues:', safeSelectedLeagues.value)
console.log('availableLeagues count:', props.availableLeagues?.length)

function toggleSeason(season) {
  let newSeasons = [...safeSelectedSeason.value]
  const idx = newSeasons.indexOf(season)
  if (idx === -1) {
    newSeasons.push(season)
  } else {
    newSeasons.splice(idx, 1)
  }
  console.log('Toggling season:', season, 'New seasons:', newSeasons)
  emit('update:selectedSeason', newSeasons)
}

function toggleLeague(leagueId) {
  let newLeagues = [...safeSelectedLeagues.value]
  const idx = newLeagues.indexOf(leagueId)
  if (idx === -1) {
    newLeagues.push(leagueId)
  } else {
    newLeagues.splice(idx, 1)
  }
  console.log('Toggling league:', leagueId, 'New leagues:', newLeagues)
  emit('update:selectedLeagues', newLeagues)
}

function toggleAllLeagues() {
  const allLeagueIds = props.availableLeagues.map(league => league.id)
  
  if (safeSelectedLeagues.value.length === allLeagueIds.length) {
    // If all are selected, deselect all
    console.log('Deselecting all leagues')
    emit('update:selectedLeagues', [])
  } else {
    // If not all are selected, select all
    console.log('Selecting all leagues')
    emit('update:selectedLeagues', allLeagueIds)
  }
}

// Check if all leagues are selected
const allLeaguesSelected = computed(() => {
  const allLeagueIds = props.availableLeagues.map(league => league.id)
  return safeSelectedLeagues.value.length === allLeagueIds.length && allLeagueIds.length > 0
})

const selectedAdditionalFilter = ref(props.selectedAdditionalFilter)

// Watch for changes in additional filter and emit updates
import { watch } from 'vue'

watch(selectedAdditionalFilter, (newFilter) => {
  console.log('Additional filter changed to:', newFilter)
  emit('update:additionalFilter', newFilter)
}, { immediate: true })

// Watch for changes in the prop and update local state
watch(() => props.selectedAdditionalFilter, (newFilter) => {
  console.log('Additional filter prop changed to:', newFilter)
  selectedAdditionalFilter.value = newFilter
}, { immediate: true })

// Watch for changes in selectedLeagues prop
watch(() => props.selectedLeagues, (newLeagues) => {
  console.log('=== selectedLeagues prop changed ===')
  console.log('New leagues:', newLeagues)
  console.log('Type:', typeof newLeagues)
  console.log('Length:', newLeagues?.length)
}, { immediate: true, deep: true })
</script>

<template>
  <aside class="bg-white text-gray-700 min-w-[300px] min-h-full p-6 rounded-l-lg flex flex-col gap-6 border-r border-gray-200">
    
    
    <!-- Additional Filters Section -->
    <div class="flex-shrink-0">
      <button 
        @click="isAdditionalFiltersExpanded = !isAdditionalFiltersExpanded"
        class="w-full flex items-center justify-between font-semibold text-lg mb-2 hover:text-pink-500 transition-colors"
      >
        <span>Additional Filters</span>
        <i class="fas fa-chevron-down text-sm transition-transform duration-200" :class="{ 'rotate-180': !isAdditionalFiltersExpanded }"></i>
      </button>
      <div 
        class="flex flex-col gap-2 overflow-hidden transition-all duration-200"
        :class="isAdditionalFiltersExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'"
      >
        <label
          v-for="(label, key) in {
            allGames: 'All Games',
            lastFiveGames: 'Last 5 Games',
            withoutLast5Games: 'Rest of Season (Without Last 5)'
          }"
          :key="key"
          class="flex items-center gap-2 cursor-pointer rounded-lg px-2 py-1"
        >
          <input
            type="radio"
            class="form-radio h-4 w-4 text-pink-500 border-pink-400 focus:ring-0"
            :value="key"
            v-model="selectedAdditionalFilter"
          />
          <span class="text-gray-700">{{ label }}</span>
        </label>
      </div>
    </div>

    <!-- Seasons Section -->
    <div class="flex-shrink-0">
      <button 
        @click="isSeasonsExpanded = !isSeasonsExpanded"
        class="w-full flex items-center justify-between font-semibold text-lg mb-2 hover:text-pink-500 transition-colors"
      >
        <span>Seasons</span>
        <i class="fas fa-chevron-down text-sm transition-transform duration-200" :class="{ 'rotate-180': !isSeasonsExpanded }"></i>
      </button>
      <div 
        class="flex flex-col gap-2 overflow-hidden transition-all duration-200"
        :class="isSeasonsExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'"
      >
        <label v-for="season in seasons" :key="season" class="flex items-center gap-2 cursor-pointer rounded-lg px-2 py-1"
          :class="safeSelectedSeason.includes(season) ? 'bg-pink-50' : ''">
          <input type="checkbox"
                 :checked="safeSelectedSeason.includes(season)"
                 @change="() => toggleSeason(season)"
                 class="form-checkbox h-4 w-4 text-pink-500 border-pink-400 focus:ring-0" />
          <span :class="safeSelectedSeason.includes(season) ? 'font-bold text-pink-500' : 'text-gray-700'">{{ season }}</span>
        </label>
      </div>
    </div>
    
    <!-- Leagues Section - Grows to fill remaining space -->
    <div class="flex-1 flex flex-col min-h-0">
      <button 
        @click="isLeaguesExpanded = !isLeaguesExpanded"
        class="w-full flex items-center justify-between font-semibold text-lg mb-2 hover:text-pink-500 transition-colors flex-shrink-0"
      >
        <span>Leagues</span>
        <i class="fas fa-chevron-down text-sm transition-transform duration-200" :class="{ 'rotate-180': !isLeaguesExpanded }"></i>
      </button>
      
      <div 
        class="flex flex-col flex-1 min-h-0 overflow-hidden transition-all duration-200"
        :class="isLeaguesExpanded ? 'opacity-100' : 'max-h-0 opacity-0'"
      >
        <!-- Toggle All Button -->
        <div class="flex-shrink-0 mb-3">
          <button
            @click="toggleAllLeagues"
            class="w-full px-3 py-2 text-sm font-medium text-pink-600 hover:bg-pink-50 rounded-md border border-pink-200 hover:border-pink-300 transition-colors flex items-center justify-center gap-2"
          >
            <i class="fas" :class="allLeaguesSelected ? 'fa-check-square' : 'fa-square'"></i>
            {{ allLeaguesSelected ? 'Deselect All' : 'Select All' }}
          </button>
        </div>
        
        <!-- Search Input -->
        <div class="flex-shrink-0 mb-3">
          <input 
            v-model="leagueSearch"
            type="text"
            placeholder="Search leagues..."
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
          />
        </div>
        
        <div class="flex flex-col gap-2 flex-1 overflow-y-auto">
          <label v-for="league in filteredLeagues" :key="league.id" class="flex items-center gap-2 cursor-pointer rounded-lg px-2 py-1 flex-shrink-0"
            :class="safeSelectedLeagues.includes(league.id) ? 'bg-pink-50' : ''">
            <input type="checkbox"
                   :checked="safeSelectedLeagues.includes(league.id)"
                   @change="() => toggleLeague(league.id)"
                   class="form-checkbox h-4 w-4 text-pink-500 border-pink-400 focus:ring-0" />
            <span :class="safeSelectedLeagues.includes(league.id) ? 'font-bold text-pink-500 text-[12px]' : 'text-gray-700 text-[12px]'">{{ league.name }}</span>
            
          </label>
          
          <!-- No results message -->
          <div v-if="filteredLeagues.length === 0 && leagueSearch.trim()" class="text-center text-gray-500 text-sm py-4">
            No leagues found matching "{{ leagueSearch }}"
          </div>
        </div>
      </div>
    </div>
  </aside>
</template> 